/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { RangeDatepicker } from '../../range'
import { UserTable } from '../UsersTable/UsersTable'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { clearSearchToken, setSearchToken, setSelectedDates, setSelectedStatuses } from '../../store/slices/userSearchSlice'
import { UserStatus } from '../../services/network'
import { CreateUserModal } from '../CreateUserModal/CreateUserModal'

function SearchBar(): JSX.Element {
  const { t } = useTranslation()

  const userSearch = useSelector((state: RootState) => state.searchUser)
  const dashboardUser = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const dispatch = useDispatch()

  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false)

  const canCreateUser = useMemo(() => {
    const role = dashboardUser?.role?.title
    if (!role) {
      return false
    }

    return role === process.env.REACT_APP_ROLE_ADMINISTRATOR || role === process.env.REACT_APP_ROLE_BACKOFFICE
  }, [dashboardUser])

  const handleCheckboxChange = (status: string): void => {
    const prevSelectedStatuses = userSearch.selectedStatuses
    dispatch(setSelectedStatuses(prevSelectedStatuses.includes(status) ? prevSelectedStatuses.filter((s) => s !== status) : [...prevSelectedStatuses, status]))
  }

  const removeStatus = (): void => {
    dispatch(setSelectedStatuses([]))
  }

  const removeSelectedDates = (): void => {
    dispatch(setSelectedDates([]))
  }

  useEffect(() => {
    eva.replace()
  })

  const status = [UserStatus.Active, UserStatus.Banned, UserStatus.Suspended, UserStatus.Verifying, UserStatus.Deleted]

  const dateFilterString = useMemo((): string => {
    const { selectedDates } = userSearch
    if (selectedDates.length <= 1) {
      return ''
    }
    const firstDay = selectedDates[0]
    const lastDay = selectedDates[1]
    const lastPart = `${selectedDates[1].getDate()} ${selectedDates[1].toLocaleString('it-IT', { month: 'short' })} ${selectedDates[1].getFullYear()}`
    if (firstDay.getFullYear() !== lastDay.getFullYear() || firstDay.getMonth() !== lastDay.getMonth()) {
      const firstPart = `${selectedDates[0].getDate()} ${selectedDates[0].toLocaleString('it-IT', { month: 'short' })} ${selectedDates[0].getFullYear()}`
      return `${firstPart} - ${lastPart}`
    } else {
      return `${selectedDates[0].getDate()} - ${lastPart}`
    }
  }, [userSearch])

  const searchInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setSearchToken({
          token: e.target.value,
          pageSize: userSearch.pageSize,
          currentPage: 1,
          selectedDates: userSearch.selectedDates,
          selectedStatuses: userSearch.selectedStatuses
        })
      )
    },
    [dispatch, userSearch.pageSize, userSearch.selectedDates, userSearch.selectedStatuses]
  )

  return (
    <Flex flexDirection={'column'} width={'100%'} height={'100vh'}>
      <HStack spacing={4} mb={4} height={'52px'}>
        <InputGroup>
          <InputLeftElement pointerEvents='none'>
            <i data-eva={'search-outline'}></i>
          </InputLeftElement>
          <Input
            autoCapitalize='sentences'
            placeholder={t('search_user_placeholder')}
            bg={'NeutralLight'}
            borderWidth='1px'
            borderColor='greyCold200'
            borderStyle='solid'
            border={'1px solid neutralDark'}
            borderRadius={'16px'}
            gap={'16px'}
            value={userSearch.token ?? ''}
            onChange={searchInput}
          />
        </InputGroup>
      </HStack>
      <HStack justifyContent={'space-between'}>
        <HStack>
          {userSearch.selectedStatuses.length > 0 ? (
            <HStack>
              <Text
                bg={'primary.400'}
                border={'1px solid primary.400'}
                borderRadius='600px'
                alignItems={'center'}
                padding={'8px 10px'}
                gap='2px'
                color={'NeutralLight'}
                fontWeight='600'
                fontSize='14px'
                lineHeight='19.6px'>
                {t(userSearch.selectedStatuses[0])} {userSearch.selectedStatuses.length > 1 ? `(+${userSearch.selectedStatuses.length})` : ''}
                <Button alignItems={'center'} size='xs' onClick={(): void => removeStatus()}>
                  <Text
                    bg={'primary.400'}
                    border={'1px solid primary.400'}
                    borderRadius='600px'
                    alignItems={'center'}
                    padding={'2px 0px'}
                    color={'NeutralLight'}
                    fontWeight='600'
                    fontSize='14px'
                    lineHeight='19.6px'>
                    X
                  </Text>
                </Button>
              </Text>
            </HStack>
          ) : (
            <div />
          )}
          {userSearch.selectedDates.length > 1 ? (
            <Text
              bg={'primary.400'}
              border={'1px solid primary.400'}
              borderRadius='600px'
              alignItems={'center'}
              padding={'8px 10px'}
              gap='2px'
              color={'NeutralLight'}
              fontWeight='600'
              fontSize='14px'
              lineHeight='19.6px'>
              {dateFilterString}
              <Button alignItems={'center'} size='xs' onClick={(): void => removeSelectedDates()}>
                <Text
                  bg={'primary.400'}
                  border={'1px solid primary.400'}
                  borderRadius='600px'
                  alignItems={'center'}
                  padding={'2px 0px'}
                  color={'NeutralLight'}
                  fontWeight='600'
                  fontSize='14px'
                  lineHeight='19.6px'>
                  X
                </Text>
              </Button>
            </Text>
          ) : (
            <div />
          )}
          <Popover placement='bottom-start'>
            <PopoverTrigger>
              <Button
                borderRadius='600px'
                padding={'8px, 10px, 8px, 10px'}
                gap='2px'
                bg='greyWarm100'
                border={'5px solid primary.400'}
                fontWeight='600'
                fontSize='14px'
                lineHeight='19.6px'
                textTransform='capitalize'>
                {t('status')}
                <i data-eva={'chevron-down'}></i>
              </Button>
            </PopoverTrigger>
            <PopoverContent borderRadius={'26px'}>
              <PopoverBody display={'flex'} flexDirection='column' gap={'26px'} padding={'20px'}>
                {status.map((s) => (
                  <Checkbox
                    key={s}
                    variant={'primary'}
                    value={s}
                    isChecked={userSearch.selectedStatuses.includes(s)}
                    onChange={(): void => handleCheckboxChange(s)}>
                    {t(s)}
                  </Checkbox>
                ))}
              </PopoverBody>
            </PopoverContent>
          </Popover>
          <RangeDatepicker
            buttonText='birth_date'
            selectedDates={userSearch.selectedDates}
            onDateChange={(dates: Date[]): void => {
              dispatch(setSelectedDates(dates))
            }}></RangeDatepicker>
        </HStack>
        {canCreateUser && (
          <Button
            variant={'outline'}
            textOverflow={'ellipsis'}
            overflow={'hidden'}
            textAlign={'center'}
            minWidth={'125px'}
            color={'primary.400'}
            borderColor={'primary.400'}
            onClick={(): void => setIsCreateUserModalOpen(true)}>
            {t('createUserModal.button')}
          </Button>
        )}
      </HStack>
      <Grid flexDirection='column' overflow={'auto'} height={'80%'} mt={4}>
        <UserTable
          query={userSearch.token ?? ''}
          startDate={userSearch.selectedDates[0] ? `${userSearch.selectedDates[0]}` : ''}
          endDate={userSearch.selectedDates[1] ? `${userSearch.selectedDates[1]}` : ''}
          selectedStatuses={userSearch.selectedStatuses}
        />
      </Grid>
      {canCreateUser && (
        <CreateUserModal
          isOpen={isCreateUserModalOpen}
          onClose={(): void => {
            setIsCreateUserModalOpen(false)
          }}
          onUserCreated={(): void => {
            dispatch(clearSearchToken())
          }}
        />
      )}
    </Flex>
  )
}

export { SearchBar }
